import { isAxiosError } from 'axios'

import { handleException } from 'clients/exception-handling'
import type { CatalogService } from 'features/catalog/service'
import type {
  CatalogPageState,
  CatalogQueryPanel,
} from 'features/catalog/types'
import { useCatalogQueryStore } from 'features/catalog/yogiql-store'
import { createDefaultPanel } from 'features/charts-v2/store'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import {
  translateV1FiltersToPanel,
  warnYogiQLFallback,
} from 'features/charts-v2/utils'
import {
  type FeedbackQueryCodec,
  fieldsToConditions,
  queryToPanel,
  toFeedbackQuery,
} from 'features/feedback/feedback-query'
import type { FilterOption } from 'features/filters/types'
import type { Filters } from 'types/project'

// ---------------------------------------------------------------------------
// The bodies `useCatalogFilterHash` reads and writes. Every hub reads through
// `/client/catalog/yogiql/page-state`; off `enhanced_dialect` the body is v2
// panels, under it the YogiQL state. A key the shared hash route wrote is
// converted in memory and never written back in its own format.
// ---------------------------------------------------------------------------

type HashHydratePayload = { panels: ChartPanel[]; activePanelId: string | null }

const PAGE = 'catalog'

const readPanels = (
  panels: CatalogQueryPanel[],
  options: FilterOption[],
): ChartPanel[] =>
  panels.map((panel, index) => queryToPanel(panel, index, options))

/** The v2 body for any key, off the flag. `unreadable` starts fresh, which
 *  drops the dead key from the URL. */
export async function readCatalogHashBody(
  catalogService: CatalogService,
  projectId: string,
  key: string,
  whenOptions: () => Promise<FilterOption[] | null>,
): Promise<Record<string, unknown>> {
  const read = await catalogService.loadYogiQL(projectId, key)
  switch (read.format) {
    case 'v2':
    case 'v1':
      return read.body
    case 'yogiql': {
      const options = await whenOptions()
      if (!options) {
        warnYogiQLFallback('catalog filter options failed, starting fresh')
        return { unreadable: 'filter options unavailable' }
      }
      const { panels, activePanelId } = read.state
      return { v2: { panels: readPanels(panels, options), activePanelId } }
    }
    case 'unreadable':
      warnYogiQLFallback(
        `catalog state unreadable, starting fresh: ${read.reason}`,
      )
      return { unreadable: read.reason }
    case 'not_found':
      warnYogiQLFallback('catalog state not found on this hub, starting fresh')
      return { unreadable: 'not_found' }
  }
}

/** The body for any key, under the flag: the state itself, a legacy body
 *  as stored, or `unreadable`. */
export async function readCatalogPageState(
  catalogService: CatalogService,
  projectId: string,
  key: string,
): Promise<Record<string, unknown>> {
  const read = await catalogService.loadYogiQL(projectId, key)
  if (read.format === 'yogiql') return { yogiql: read.state }
  if (read.format === 'v2' || read.format === 'v1') return read.body
  const reason = read.format === 'unreadable' ? read.reason : 'not_found'
  warnYogiQLFallback(`catalog state unreadable, starting fresh: ${reason}`)
  return { unreadable: reason }
}

/** Saves the state under the flag. A 422 (e.g. a theme renamed since the
 *  options loaded) goes through the shared route as v2 panels instead. */
export function saveCatalogPageState(
  catalogService: CatalogService,
  projectId: string,
  state: CatalogPageState,
  options: FilterOption[],
): Promise<string> {
  return catalogService.saveYogiQL(projectId, state).catch((error: unknown) => {
    if (!isAxiosError(error) || error.response?.status !== 422) throw error
    handleException(error, {
      operation: 'catalog.state.save',
      disposition: 'expected',
      isExpectedError: (cause) =>
        isAxiosError(cause) && cause.response?.status === 422,
      reason:
        'A compiler refusal falls back to the existing shared hash route; fallback rejection propagates.',
    })
    warnYogiQLFallback(
      'catalog page refused, saved through the shared hash route',
    )
    const v2 = {
      panels: readPanels(state.panels, options),
      activePanelId: state.activePanelId,
    }
    return catalogService.saveFilterState(projectId, {
      page: PAGE,
      filterString: JSON.stringify({ v2 }),
    })
  })
}

// The panel a legacy body holds; its v1 `filters` key is a permanent
// fallback for links that predate the v2 key.
function legacyPanels(
  body: Record<string, unknown>,
): HashHydratePayload | null {
  const v2 = body['v2'] as HashHydratePayload | undefined
  if (v2?.panels?.length) return v2
  const v1Filter = (body['filters'] as Filters[] | undefined)?.[0]
  if (!v1Filter) return null
  const defaultPanel = createDefaultPanel(0)
  const panel = {
    ...defaultPanel,
    ...translateV1FiltersToPanel(v1Filter, defaultPanel),
  }
  return { panels: [panel], activePanelId: defaultPanel.id }
}

/** Hydrates the v2 store from a `readCatalogHashBody` body. */
export function hydrateCatalogHashBody(
  body: Record<string, unknown>,
  hydrateStore: (state: HashHydratePayload) => void,
): void {
  if (body['unreadable']) {
    const fresh = createDefaultPanel(0)
    hydrateStore({ panels: [fresh], activePanelId: fresh.id })
    return
  }
  const legacy = legacyPanels(body)
  if (legacy) hydrateStore(legacy)
}

/** Hydrates the query store from a `readCatalogPageState` body; returns the
 *  panel now held, or null when the body started fresh or held nothing. */
export function hydrateCatalogYogiQLBody(
  body: Record<string, unknown>,
  codec: FeedbackQueryCodec,
): CatalogQueryPanel | null {
  const store = useCatalogQueryStore.getState()
  const state = body['yogiql'] as CatalogPageState | undefined
  if (body['unreadable'] || (state && state.panels.length === 0)) {
    store.resetCatalogQuery()
    return null
  }
  if (state) {
    const { panels, activePanelId } = state
    const known = panels.some((p) => p.id === activePanelId)
    store.hydrate({
      panels,
      activePanelId: known ? activePanelId : panels[0]!.id,
    })
  } else {
    const legacy = legacyPanels(body)?.panels[0]
    if (!legacy) return null
    const query = toFeedbackQuery(fieldsToConditions(legacy, codec))
    store.hydrate({
      panels: [{ id: legacy.id, query }],
      activePanelId: legacy.id,
    })
  }
  return useCatalogQueryStore.getState().panels[0] ?? null
}
